import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject, Observable, of } from "rxjs";
import { first } from "rxjs/operators";
import { TokenService } from "src/app/auth/token.service";
import { ConfirmDialogComponent } from "src/app/shared/confirm-dialog/confirm-dialog.component";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { ValidationError } from "src/app/shared/Models/ValidationError";
import { ToastService } from "src/app/shared/toast.service";
import { Coordinate } from "../../../../shared/Models/buildingTableViewModel";
import { SurveyStatus } from "../../../../shared/Models/enums/SurveySurveyStatus";
import { Survey } from "../../../../shared/Models/read/Survey";
import { RejectSurveyDto } from "../../../../shared/Models/write/RejectSurveyDto";
import {
  SaveValidateOutsideSurveyDto,
  ValidateOutsideSurveyDto,
} from "../../../../shared/Models/write/ValidateSurveyDto";
import { OutsideSurveyService } from "../../../../shared/services/outside-survey.service";
import { SavingStateService } from "../../shared/services/saving-state.service";
import { OUTSIDE_SURVEY_VALIDATE } from "../config/outside-survey.magic-strings";
import { CoordinatesRegexString } from "src/app/shared/regex/regexes";
import { Router } from "@angular/router";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";

@Component({
  selector: "app-outside-survey-validate",
  templateUrl: "./outside-survey-validate.component.html",
  styleUrls: ["./outside-survey-validate.component.scss"],
})
export class OutsideSurveyValidateComponent implements OnInit, OnDestroy {
  tabId = OUTSIDE_SURVEY_VALIDATE;
  @Input() survey: Survey;
  @Input() isAuthCanWrite: boolean;
  @Input() performValidation: () => boolean;
  remarks: string;
  surveyStatusEnum = SurveyStatus;
  coordsValidated = false;
  coordFormGroup = new FormGroup({
    x: new FormControl(null, [
      Validators.required,
      Validators.min(0.000000001),
      Validators.max(999999999),
    ]),
    y: new FormControl(null, [
      Validators.required,
      Validators.min(0.000000001),
      Validators.max(999999999),
    ]),
  });

  constructor(
    private outsideSurveyService: OutsideSurveyService,
    private modalService: NgbModal,
    private cd: ChangeDetectorRef,
    private savingStateService: SavingStateService,
    private toastService: ToastService,
    private router: Router,
    private permissionsService: PermissionsService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.remarks = this.survey.validatorRemark
      ? this.survey.validatorRemark
      : "";
    let coordinates = this.getMainCoordinate(this.survey.building.coordinates);
    if (coordinates)
      this.coordFormGroup.patchValue({
        x: coordinates.posX,
        y: coordinates.posY,
      });

    this.cd.detectChanges();
  }

  buildingNeedsInsideSurvey(survey: Survey) {
    if (
      survey.building.buildingType === BuildingTypeEnum.EmptyParcel ||
      survey.building.buildingType === BuildingTypeEnum.NoAccess ||
      survey.building.buildingType === BuildingTypeEnum.NotInUse
    )
      return false;

    if (!!survey.building.buildingGroupId) return true;
    if (survey.isMDU) return true;
    return false;
  }

  onValidate() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    var validated = this.performValidation();

    if (!validated) {
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.invalid:Not valid`,
        $localize`:@@validation.survey.invalidtabs:Not all tabs have been verified.`
      );
    } else {
      const validateSurveyDto = new ValidateOutsideSurveyDto({
        surveyId: this.survey.id,
        surveyStatus: this.buildingNeedsInsideSurvey(this.survey)
          ? SurveyStatus.InsidePending
          : SurveyStatus.OutsideDone,
        validatorRemark: this.remarks,
        coordinates: [this.getCoordinates()],
      });

      this.savingStateService
        .saveAll(
          true,
          this.outsideSurveyService.validateSurvey(validateSurveyDto)
        )
        .pipe(first())
        .subscribe();
    }
  }

  private navigateToCommand() {
    this.router.navigateByUrl(
      "/admin/commands/" + this.survey.failedValidateSurveyCommandId
    );
  }

  private getCoordinates() {
    let coords: Coordinate;
    if (this.survey.building.coordinates.length)
      coords = this.getMainCoordinate(this.survey.building.coordinates);
    else coords = new Coordinate();

    coords.posX = this.coordFormGroup.get("x")?.value?.toString();
    coords.posY = this.coordFormGroup.get("y")?.value?.toString();
    return coords;
  }

  private getMainCoordinate(coordinates: Coordinate[]): Coordinate {
    if (coordinates.some((c) => c.isCoordinatesMiddleBuilding))
      return coordinates.find((c) => c.isCoordinatesMiddleBuilding == true);
    else return coordinates[0];
  }

  copyToClipboard(inputElement: HTMLInputElement) {
    inputElement.select();
    document.execCommand("copy");
    inputElement.setSelectionRange(0, 0);
    inputElement.blur();
  }

  validateCoordinates() {
    if (!this.isAuthCanWrite) return;
    this.coordFormGroup.markAllAsTouched();

    var coordinates = this.getCoordinates();
    var isXcorrect =
      this.isDecimal(coordinates.posX) && Number(coordinates.posX) > 0;
    var isYcorrect =
      this.isDecimal(coordinates.posY) && Number(coordinates.posY) > 0;
    if (isXcorrect && isYcorrect) {
      this.saveValidation();
      this.coordsValidated = true;
    } else {
      this.coordsValidated = false;

      if (!isXcorrect)
        this.coordFormGroup.controls["x"].setErrors({ incorrect: true });

      if (!isYcorrect)
        this.coordFormGroup.controls["y"].setErrors({ incorrect: true });

      if (!this.coordsValidated) {
        const valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.outside.coordinates:Coordinates are not correct: max 9 decimals and no 0 or empty values.`,
          $localize`:@@validation.survey.outside.coordinates.title:Outside Survey, Coordinates`
        );
        this.toastService.showWarnToast(valError.title, valError.message);
      }
    }

    return this.coordsValidated;
  }

  get disableValidate() {
    return (
      (this.survey.building.lomKey == null && !this.coordsValidated) ||
      this.survey.surveyStatus !== SurveyStatus.OutsideWaitingForValidation
    );
  }

  get isSurveyBlockedByOngoingSync(): boolean {
    if (!this.survey.hasOngoingValidateSurvey) return false;
    return !this.permissionsService.allowed(PermissionsEnum.ForceRollBack);
  }

  isDecimal(input) {
    let re = new RegExp(CoordinatesRegexString);
    return re.test(String(input));
  }

  remarkChanged(event) {
    this.remarks = event;
    this.saveValidation();
  }

  save(checkValidity?: boolean): Observable<any> {
    if (checkValidity) {
      var isValid = this.validateCoordinates();
      return of(isValid);
    }
    return of(true);
  }

  saveValidation() {
    const validateSurveyDto = new SaveValidateOutsideSurveyDto({
      surveyId: this.survey.id,
      surveyStatus: this.survey.surveyStatus,
      validatorRemark: this.remarks,
      coordinates: [this.getCoordinates()],
    });
    this.savingStateService.addOutsideSurveySaveValidation(validateSurveyDto);
  }

  onReject() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    const modalRef = this.modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = $localize`:@@validation.survey.rejectsurvey:Are you sure you want to reject this survey?`; // optional message
    modalRef.componentInstance.decision
      .pipe(first())
      .subscribe((descision: boolean) => {
        if (descision) {
          const rejectSurveyDto = new RejectSurveyDto({
            surveyId: this.survey.id,
            validatorRemark: this.remarks,
          });

          this.savingStateService
            .saveAll(
              true,
              this.outsideSurveyService.rejectSurvey(rejectSurveyDto)
            )
            .pipe(first())
            .subscribe();
        }
      });
  }
}
